import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowUpFromLine, Clock, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, dateFmt, shortAddr } from "@/lib/format";

export default function Withdraw() {
  const [balance, setBalance] = useState(0);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ amount: "", to_address: "", otp: "" });
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const load = async () => {
    const [d, w] = await Promise.all([api.get("/me/dashboard"), api.get("/me/withdrawals")]);
    setBalance(d.data.wallet.income_balance);
    setRows(w.data);
  };
  useEffect(() => { load().catch(() => toast.error("Load failed")); }, []);

  const amount = Number(form.amount || 0);
  const fee = amount * 0.05;
  const net = amount - fee;

  const sendOtp = async () => {
    if (!(amount >= 10) || !form.to_address) { toast.error("Enter a valid amount and address first"); return; }
    setSendingOtp(true);
    try {
      const { data } = await api.post("/me/withdraw/request-otp");
      setOtpSent(true);
      setSentTo(data.sent_to || "your email");
      toast.success(`Verification code sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setSendingOtp(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!otpSent) { toast.error("Please request and enter the email OTP first"); return; }
    if (!form.otp || form.otp.length < 6) { toast.error("Enter the 6-digit code from your email"); return; }
    setBusy(true);
    try {
      await api.post("/me/withdraw", { amount, to_address: form.to_address, otp: form.otp });
      toast.success("Withdrawal request submitted");
      setForm({ amount: "", to_address: "", otp: "" });
      setOtpSent(false);
      setSentTo("");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="withdraw-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><ArrowUpFromLine className="w-6 h-6 text-amber-400" /> Withdraw USDT</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <div className="text-xs text-slate-400 uppercase tracking-wide">Available Income Balance</div>
          <div className="font-mono-fin text-3xl font-extrabold text-emerald-400 mt-1">{money(balance)}</div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm text-slate-400">Amount (min $10)</label>
              <input data-testid="withdraw-amount-input" type="number" min="10" step="any" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none font-mono-fin" placeholder="10" />
            </div>
            <div>
              <label className="text-sm text-slate-400">Your USDT BEP20 Address</label>
              <input data-testid="withdraw-address-input" required value={form.to_address} onChange={(e) => setForm({ ...form, to_address: e.target.value })}
                className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none font-mono-fin text-sm" placeholder="0x..." />
            </div>
            <div className="rounded-xl bg-black/30 border border-amber-500/15 p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-400">Fee (5%)</span><span className="font-mono-fin text-amber-300">{money(fee)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">You Receive</span><span className="font-mono-fin text-emerald-400 font-bold">{money(net)}</span></div>
            </div>

            {!otpSent ? (
              <button type="button" data-testid="withdraw-send-otp-btn" onClick={sendOtp} disabled={sendingOtp || !(amount >= 10) || !form.to_address}
                className="w-full py-3 rounded-xl font-bold border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 flex items-center justify-center gap-2">
                <ShieldCheck className="w-4 h-4" /> {sendingOtp ? "Sending code..." : "Send Email Verification Code"}
              </button>
            ) : (
              <div data-testid="withdraw-otp-section">
                <label className="text-sm text-slate-400">Email OTP (sent to {sentTo})</label>
                <input data-testid="withdraw-otp-input" inputMode="numeric" maxLength={6} required value={form.otp}
                  onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g, "") })}
                  className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none font-mono-fin tracking-[0.5em] text-center text-lg" placeholder="••••••" />
                <button type="button" data-testid="withdraw-resend-otp-btn" onClick={sendOtp} disabled={sendingOtp}
                  className="text-xs text-amber-400 hover:text-amber-300 mt-2">{sendingOtp ? "Resending..." : "Resend code"}</button>
              </div>
            )}

            <button data-testid="withdraw-submit-btn" disabled={busy || !otpSent} className="gold-btn w-full py-3 rounded-xl font-bold disabled:opacity-60">
              {busy ? "Submitting..." : "Request Withdrawal"}
            </button>
            <p className="text-xs text-slate-500 text-center">A verification code will be emailed to you. Processing time: 24–72 hours</p>
          </form>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" /> Withdrawal History</h3>
          {rows.length === 0 ? <p className="text-slate-500 text-sm">No withdrawals yet.</p> : (
            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.id} className="rounded-xl bg-black/30 border border-amber-500/15 p-4" data-testid={`withdraw-row-${r.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-mono-fin font-bold gold-text">{money(r.amount)}</div>
                      <div className="text-xs text-slate-500 mt-1">Net: {money(r.net)} → {shortAddr(r.to_address)}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${r.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : r.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{r.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">{dateFmt(r.created_at)}{r.txid && <span className="ml-2 text-sky-400 font-mono-fin">{shortAddr(r.txid)}</span>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
